import { motion } from "framer-motion";
import { useEnquiry } from "@/components/site/EnquiryProvider";
import { ArrowUpRight } from "lucide-react";

const services = [
  {
    slug: "art-gallery",
    title: "Art Gallery",
    tagline: "A curated destination for contemporary Indian art.",
    body: "Thoughtfully curated exhibitions featuring emerging and established artists \u2014 contemporary paintings, mixed media and traditional practices reimagined for today\u2019s collectors. Includes bespoke art advisory for collectors, architects, hospitality projects and interior designers.",
    tags: ["Art Gallery Gurgaon", "Contemporary Art", "Curated Exhibitions", "Art Collectors"],
  },
  {
    slug: "heritage-tours",
    title: "Heritage Tours",
    tagline: "Travel through India\u2019s living heritage.",
    body: "Immersive cultural journeys led by researchers, artists and historians. From temple architecture and royal palaces to ancient cities and craft villages \u2014 a classroom without walls for discerning travellers seeking authentic encounters.",
    tags: ["Luxury Heritage Tours", "Cultural Travel", "Temple Architecture", "Boutique Journeys"],
  },
  {
    slug: "art-classes",
    title: "Art Classes",
    tagline: "Creativity for every generation.",
    body: "Nurturing programmes for children, teenagers, adults and aspiring artists \u2014 drawing, painting, mixed media, traditional Indian art and visual storytelling. An environment that values imagination over imitation.",
    tags: ["Art Classes Gurgaon", "Painting Classes", "Children\u2019s Art", "Adult Workshops"],
  },
  {
    slug: "corporate-workshops",
    title: "Corporate Workshops",
    tagline: "Creativity that inspires teams.",
    body: "Collaborative murals, storytelling through art, heritage-inspired creativity and leadership sessions rooted in culture. Bespoke programmes that build collaboration, innovation and employee well-being \u2014 customised to your organisation\u2019s goals.",
    tags: ["Team Building", "Employee Engagement", "Corporate Creativity", "Wellness"],
  },
  {
    slug: "space-styling",
    title: "Space Styling",
    tagline: "Curated spaces with character.",
    body: "Bespoke art consultancy and styling for residences, hospitality projects and commercial spaces. Original artworks, sculptures, textures and heritage elements come together in environments that inspire people and celebrate culture.",
    tags: ["Art Consultancy", "Hospitality Art", "Curated Interiors", "Interior Styling"],
  },
  {
    slug: "art-advisory",
    title: "Art Advisory & Collections",
    tagline: "Investment-grade counsel for collectors.",
    body: "Art acquisition for private collectors, corporate collections, hotels and hospitality projects, commissioned artworks and sculpture installations \u2014 with investment-grade advisory throughout the collecting journey.",
    tags: ["Art Advisory", "Art Acquisition", "Corporate Collections", "Commissioned Art"],
  },
];

function ServiceCard({ s, i }) {
  const { openEnquiry } = useEnquiry();
  const enquire = () =>
    openEnquiry({ is_service: true, slug: `service-${s.slug}`, title: s.title, tagline: s.tagline });

  return (
    <motion.article
      id={`service-${s.slug}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.75, delay: (i % 2) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group relative border-t border-ink-soft/25 py-12 md:py-14 grid grid-cols-[auto,1fr] gap-6 md:gap-10 scroll-mt-24"
      data-testid={`service-${s.slug}`}
    >
      <span className="font-serif text-3xl md:text-5xl text-ochre/70 leading-none">
        {String(i + 1).padStart(2, "0")}
      </span>
      <div>
        <div className="flex items-start justify-between gap-6">
          <div>
            <h3 className="font-serif text-2xl md:text-4xl text-ink leading-tight tracking-tight group-hover:text-ochre transition-colors">
              {s.title}
            </h3>
            <p className="mt-2 font-serif italic text-lg md:text-xl text-ink-soft">
              {s.tagline}
            </p>
          </div>
          <button
            type="button"
            onClick={enquire}
            data-testid={`service-enquire-${s.slug}`}
            data-cursor="Enquire"
            aria-label={`Enquire about ${s.title}`}
            className="shrink-0 h-12 w-12 md:h-14 md:w-14 flex items-center justify-center border border-ink-soft/40 text-ink transition-colors group-hover:bg-ochre group-hover:text-paper group-hover:border-ochre"
          >
            <ArrowUpRight className="h-5 w-5" />
          </button>
        </div>

        <p className="mt-5 font-body text-base md:text-lg text-ink-soft leading-relaxed max-w-2xl">
          {s.body}
        </p>

        <div className="mt-6 flex flex-wrap gap-2">
          {s.tags.map((t) => (
            <span
              key={t}
              className="font-body text-[10px] tracking-[0.15em] uppercase text-ink-soft border border-ink-soft/25 px-2.5 py-1"
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </motion.article>
  );
}

export default function Studio() {
  const { openEnquiry } = useEnquiry();

  return (
    <section
      id="studio"
      className="relative py-24 md:py-36 bg-sand overflow-hidden"
      data-testid="studio-section"
    >
      <span className="pointer-events-none select-none absolute -top-8 -left-8 md:-top-16 font-serif italic text-[22vw] leading-none text-ink/[0.04] whitespace-nowrap">
        Studio
      </span>

      <div className="max-w-screen-2xl mx-auto px-6 md:px-10 relative">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-16 md:mb-24">
          <div className="md:col-span-8">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="font-body text-[11px] tracking-[0.35em] uppercase text-ochre mb-5"
            >
              The Studio &mdash; What We Do
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="font-serif text-4xl sm:text-5xl lg:text-7xl leading-[1] text-ink tracking-tight"
            >
              A cultural
              <br />
              <span className="italic text-ochre">ecosystem.</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="md:col-span-4 font-body text-base text-ink-soft leading-relaxed"
          >
            Beyond original artworks, The Art Lounge Nirvana curates exhibitions,
            heritage journeys, workshops and interiors &mdash; every experience designed
            to inspire curiosity and celebrate craftsmanship.
          </motion.p>
        </div>

        <div>
          {services.map((s, i) => (
            <ServiceCard key={s.slug} s={s} i={i} />
          ))}
        </div>

        <div className="border-t border-ink-soft/25 pt-10 mt-2 flex flex-wrap items-center justify-between gap-6">
          <p className="font-serif italic text-xl md:text-2xl text-ink max-w-xl leading-snug">
            &ldquo;Art should not merely be viewed &mdash; it should be experienced,
            understood and lived.&rdquo;
          </p>
          <button
            type="button"
            onClick={() => openEnquiry(null)}
            data-testid="studio-general-enquire"
            data-cursor="Enquire"
            className="group inline-flex items-center gap-3 bg-ink px-8 py-4 font-body text-xs tracking-[0.25em] uppercase text-paper transition-colors hover:bg-ochre"
          >
            Talk to the studio
            <span className="transition-transform group-hover:translate-x-1">&rarr;</span>
          </button>
        </div>
      </div>
    </section>
  );
}
